import { BreakPoint } from '@/hooks/useBreakPoint/consts';
import useDataLayer from '@/hooks/useDataLayer/useDataLayer';
import { HeaderLink } from '@/hooks/useHeader/types';
import useIsMobile from '@/hooks/useIsMobile/useIsMobile';
import clsx from 'clsx';
import { useEffect } from 'react';
import { extractClickableProps } from '../../helpers/extractClickableProps';
import Button from '../Button/Button';
import { ButtonSize } from '../Button/consts';
import LandingPageHeader from '../LandingPageHeader/LandingPageHeader';
import LinkAddon from '../NewHeader/LinkAddon/LinkAddon';
import NewHeader from '../NewHeader/NewHeader';
import PageHeader from '../NewHeader/PageHeader/PageHeader';
import { HEADER_LOGO_HEIGHTS } from '../NewHeader/consts';
import { NewHeaderProps } from '../NewHeader/types';
import ProjectLogo from '../ProjectLogo/ProjectLogo';
import styles from './LegacyHeader.module.scss';
import { LegacyHeaderVariant } from './consts';
import {
  convertProjectName,
  convertVariant,
  createPageHeaderLink,
} from './helpers';
import { LegacyHeaderProps } from './types';

const LegacyHeader = ({
  contents,
  ctaText,
  headerTitle,
  headerTitleLink,
  hideGlobalHeader,
  icon,
  invert = false,
  isFullWidth = false,
  isPageHeaderVisible,
  logo,
  logoLink = '/',
  navItems,
  noBorderBottom,
  noSticky,
  pageHeaderLogo,
  pageHeaderLogoLink,
  pageHeaderType,
  pageNavItemMain,
  parentPagePath,
  showIcon,
  siteVersion = 2,
  variant = LegacyHeaderVariant.Privatkunde,
  ...props
}: LegacyHeaderProps) => {
  const { pushDataLayerEvent } = useDataLayer();
  const isMobile = useIsMobile(BreakPoint.LG);

  const clickableProps = extractClickableProps(props);

  const fallbackLogo = (
    <ProjectLogo
      height={
        HEADER_LOGO_HEIGHTS[convertProjectName(variant)][
          isMobile ? 'mobile' : 'desktop'
        ]
      }
      project={convertProjectName(variant)}
    />
  );

  const pageHeaderLinks = contents?.length
    ? contents
        .filter(
          ({ componentName, props }) =>
            componentName.startsWith('navItems') &&
            props.link in props &&
            props[props.link as keyof typeof props]
        )
        .map<HeaderLink>(createPageHeaderLink)
    : navItems?.map<HeaderLink>((item) => ({
        isInternalUrl: true,
        title: item.title,
        url: item.page,
      }));

  const pageHeaderButton = !ctaText ? undefined : (
    <Button
      {...clickableProps}
      clickContext="Page Header"
      icon={icon}
      showIcon={showIcon}
      size={ButtonSize.Small}
    >
      {ctaText}
    </Button>
  );

  const convertedVariant = convertVariant(variant);

  const convertedProps: NewHeaderProps = {
    hasPageHeader:
      isPageHeaderVisible ?? (!!pageHeaderLinks?.length || !!headerTitle),
    hasPreHeader: [
      'enercity',
      'enercity-gwk',
      'ecg',
      'eng',
      'commune',
    ].includes(convertedVariant),
    isFullWidth: isFullWidth,
    isInverted: invert,
    isSticky: !noSticky,
    renderMainHeaderAddons:
      convertedVariant === 'eng'
        ? ({ shouldHideContent }) => (
            <div
              className={clsx(styles.addonWrapper, {
                [styles.isHidden]: shouldHideContent,
                [styles.visibleOnMobile]: true,
              })}
            >
              <LinkAddon
                icon="user/account"
                title="NetzPortal"
                url="https://startseite.portal.enercity-netz.de"
                isInternalUrl={false}
              />
            </div>
          )
        : undefined,
    renderPageHeader: (props) => (
      <PageHeader
        {...props}
        addonsSlot={!!pageHeaderButton && <>{pageHeaderButton}</>}
        links={pageHeaderLinks}
        overviewUrl={headerTitleLink ?? parentPagePath}
        title={headerTitle}
      />
    ),
    variant: convertedVariant,
  };

  useEffect(() => {
    if (!hideGlobalHeader) {
      pushDataLayerEvent({ siteVersion });
    }
  }, [hideGlobalHeader, pushDataLayerEvent, siteVersion]);

  return hideGlobalHeader ? (
    <LandingPageHeader
      {...props}
      ctaText={ctaText}
      items={pageHeaderLinks}
      logo={logo ?? fallbackLogo}
    />
  ) : (
    <NewHeader {...convertedProps} />
  );
};

export default LegacyHeader;
